import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';

import { StateCard } from '@/components/clinical';
import { Button } from '@/components/ui/button';
import { AvailabilityCalendar } from '@/features/availability/components/calendar/AvailabilityCalendar';
import { PractitionerSwitcher } from '@/features/availability/components/triage/PractitionerSwitcher';
import { TriageView } from '@/features/availability/components/triage/TriageView';
import { MyPractitionerLayout } from '@/features/practitioners/components/shared/MyPractitionerLayout';
import { useAuthStore } from '@/stores/authStore';

const searchSchema = z.object({
  tab: z.enum(['calendar', 'triage']).optional(),
  practitionerId: z.string().optional(),
});

export const Route = createFileRoute('/app/availability/')({
  // Block CRUD now lives on /app/profile (Schedule tab) for psychologists.
  // Admins keep this route for the practitioner-switcher + triage flow.
  // Bounce non-admin self-views to profile so deep-links and bookmarks
  // don't dead-end.
  beforeLoad: () => {
    const user = useAuthStore.getState().user;
    const isAdmin =
      user?.role === 'ba_admin' || user?.role === 'ba_super_admin' || user?.role === 'super_admin';
    if (!isAdmin) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/app/profile' });
    }
  },
  component: ClinicalMyAvailabilityScreen,
  validateSearch: searchSchema,
  staticData: { crumb: 'Availability triage' },
});

function ClinicalMyAvailabilityScreen() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isAdmin =
    user?.role === 'ba_admin' || user?.role === 'ba_super_admin' || user?.role === 'super_admin';
  const tab = search.tab ?? 'calendar';

  // ── Admin: Triage tab ───────────────────────────────────────────────────
  if (tab === 'triage' && isAdmin && user.businessAssociateId) {
    return (
      <div className="space-y-4">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="font-display text-display-lg text-accent-ink tracking-tight">
            Availability triage
          </h1>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void navigate({ to: '/app/availability', search: { tab: 'calendar' } });
            }}
          >
            Back to calendar
          </Button>
        </header>
        <TriageView
          businessAssociateId={user.businessAssociateId}
          onPractitionerSelect={(id) => {
            void navigate({
              to: '/app/availability',
              search: { tab: 'calendar', practitionerId: id },
            });
          }}
        />
      </div>
    );
  }

  // ── Admin: Calendar tab ─────────────────────────────────────────────────
  // Admins don't have a clinician profile, so we don't gate behind
  // `MyPractitionerLayout`. Instead, show the practitioner switcher and an
  // empty state until they pick a practitioner.
  if (isAdmin && user.businessAssociateId) {
    const practitionerId = search.practitionerId;
    return (
      <div className="space-y-4">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="font-display text-display-lg text-accent-ink tracking-tight">
            Practitioner availability
          </h1>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void navigate({ to: '/app/availability', search: { tab: 'triage' } });
            }}
          >
            Triage view
          </Button>
        </header>
        <PractitionerSwitcher
          businessAssociateId={user.businessAssociateId}
          selectedPractitionerId={practitionerId ?? null}
          onChange={(id) => {
            void navigate({
              to: '/app/availability',
              search: { practitionerId: id },
            });
          }}
        />
        {practitionerId ? (
          <AvailabilityCalendar
            practitionerId={practitionerId}
            canManageAssignments={isAdmin}
            canManageBlocks={false}
          />
        ) : (
          <StateCard
            variant="empty"
            headline="Pick a practitioner"
            description="Choose a practitioner from the dropdown above to view and edit their availability, or jump to the triage view to see who needs attention."
          />
        )}
      </div>
    );
  }

  // ── Practitioner self-view ──────────────────────────────────────────────
  return (
    <MyPractitionerLayout>
      {(selfPractitionerId) => {
        const practitionerId = search.practitionerId ?? selfPractitionerId;
        const isOwnCalendar = practitionerId === selfPractitionerId;
        return (
          <div className="space-y-4">
            <h1 className="font-display text-display-lg text-accent-ink tracking-tight">
              My availability
            </h1>
            <AvailabilityCalendar
              practitionerId={practitionerId}
              canManageAssignments={isAdmin}
              canManageBlocks={isOwnCalendar}
            />
          </div>
        );
      }}
    </MyPractitionerLayout>
  );
}
